import { Trans, useLingui } from '@lingui/react/macro';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { TEMPLATE_RESOLUTIONS } from 'modules/manager/domain/template-resolutions';
import { useCreateMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  MediaManagerTemplateFormValues,
  getDefaultMediaKindOption,
  getMediaKindOption,
} from 'modules/manager/ui/components/forms/media-manager-template-form';

interface Args {
  templateGroupId: number;
  existingNames: string[];
  templatesCount: number;
  title?: string;
}

/** What the form starts with: a media kind chosen before the dialog opened (IMGM-1518). */
export interface CreateTemplateInitial {
  mediaKind?: MediaKind;
  /**
   * The championship «Медиа» tab counts a kind as covered only by its *default* template of the
   * set — a non-default one leaves the kind at «Нет шаблона». Checked when the dialog comes from that
   * tab's «Создать шаблон»: the kind has no template there yet, so nothing else is default.
   */
  isDefaultForKind?: boolean;
}

/**
 * The group, its counter in the group list and the championship «Медиа» tab refetch by the
 * mutation's tags — no «reload yourself» callback (IMGM-1515).
 */
function useCreateTemplateModal({ templateGroupId, existingNames, templatesCount, title }: Args) {
  const { t } = useLingui();
  const [createTemplate, { isLoading }] = useCreateMediaManagerTemplateMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [initial, setInitial] = useState<CreateTemplateInitial>({});
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next?: CreateTemplateInitial) => {
    setError(null);
    setInitial(next ?? {});
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      setError(null);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await createTemplate({
          name: values.name.trim(),
          objects: [],
          version: '5.3.0',
          templateGroupId,
          sportType: values.sportType?.value ?? null,
          order: templatesCount,
          width: resolution.width,
          height: resolution.height,
          mediaKind: values.mediaKind.value,
          isDefaultForKind: values.isDefaultForKind,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        }).unwrap();
        close();
      } catch (err) {
        console.error('Failed to create media manager template:', err);
        setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось создать шаблон, попробуйте ещё раз`);
      }
    },
    [createTemplate, templateGroupId, templatesCount, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          {title ?? <Trans>Создать шаблон</Trans>}
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: '',
            resolution: DEFAULT_RESOLUTION_OPTION,
            sportType: null,
            mediaKind: initial.mediaKind ? getMediaKindOption(initial.mediaKind) : getDefaultMediaKindOption(),
            isDefaultForKind: initial.isDefaultForKind ?? false,
            subtype: null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useCreateTemplateModal;
